//this file will contain wrapper functions for http get/post.
import axios, { HttpStatusCode } from "axios";

import store from "../redux/store";
import { ToggleActionModal } from "../redux/uiReducer";

import { config } from "../Helpers/config";
import { helperText } from "../Helpers/helperText";
import {
  DecryptUsingAES256,
  EncryptUsingAES256,
  GetLocalStorageData,
  IsStringEmpty,
} from "../Helpers/Utilities";

const { MultipleLogin, sessionTimeout } = helperText.global;

const defaultTimeout = 60_000;
let postServiceTimeout = null;
let saveServiceTimeout = null;
let postServiceWithHeaderTimeout = null;
let getServiceTimeout = null;
const TIMEOUT_ERROR = "timeout error";

const ExcludeURLList = [
  "https://example.com/api/Common/commonFileuploaddata",
  "https://example.com/api/Users/UploadProfilePicture",
  "https://example.com/api/Common/downloadattachedfile",
  `${window.origin}/api/acs/DropSession`,
];

class WebServiceWrapper {
  constructor() {
    const service = axios.create();

    // Request interceptor
    service.interceptors.request.use(this.HandleRequest, function (error) {
      // Do something with request error
      return Promise.reject(error);
    });

    // Response interceptor
    service.interceptors.response.use(
      this.HandleSuccessResponse,
      this.HandleErrorResponse
    );

    service.defaults.timeout = defaultTimeout;
    this.service = service;
  }

  buildUrl = (path) => {
    return `${config.apiUrl}${path}`;
  };

  buildLongRunUrl = (path) => {
    return `${config.longRunningUrl}${path}`;
  };

  /* <summary>
  date: 10-04-2023
  Name: GM
  description: Encrypt the request
  <summary>*/
  HandleRequest(req) {
    const authToken = GetLocalStorageData("authToken");

    // Check if the request url includes any of the excluded URLs
    const exludeFound = ExcludeURLList.filter((element) => {
      return req.url.includes(element);
    });

    // Handle request URLs which are to be encrypted
    if (!(exludeFound && exludeFound.length > 0)) {
      if (req.method === "get" && req.url.indexOf("?") > 0) {
        const encriptURL =
          req.url.substr(0, req.url.indexOf("?") + 1) +
          EncryptUsingAES256(
            req.url.substr(req.url.indexOf("?") + 1, req.url.length)
          );

        const cloneReq = req;
        cloneReq.url = encriptURL;
        if (
          authToken &&
          window.location.origin.includes("localhost") &&
          !req.url.includes("AuthenticateUser")
        ) {
          cloneReq.headers = {
            ...cloneReq.headers,
            Authorization: `Bearer ${authToken}`,
          };
        }

        return cloneReq;
      }

      if (req.method === "post" && (req.data || req.data.length > 0)) {
        const cloneReq = req;
        const payload = EncryptUsingAES256(JSON.stringify(req.data));
        const encryptedPayload = {
          payload,
        };
        cloneReq.data = encryptedPayload;
        if (
          authToken &&
          window.location.origin.includes("localhost") &&
          !req.url.includes("AuthenticateUser")
        ) {
          cloneReq.headers = {
            ...cloneReq.headers,
            Authorization: `Bearer ${authToken}`,
          };
        }
        return cloneReq;
      }
    }

    return req;
  }

  /* <summary>
  date: 29-11-2022
  Name: Gm
  description: Handle Success Response from WebAPI
  <summary>*/
  HandleSuccessResponse(response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data

    const exludeFound = ExcludeURLList.filter((element) => {
      return response.config.url.includes(element);
    });

    if (!(exludeFound && exludeFound.length > 0)) {
      const cloneRes = response;

      const decryptedData = IsStringEmpty(response.data)
        ? ""
        : JSON.parse(DecryptUsingAES256(response.data));
      cloneRes.data = decryptedData;

      return {
        status: HttpStatusCode.Ok,
        error: null,
        data: cloneRes.data,
        headers: cloneRes.headers,
      };
    }

    return {
      status: HttpStatusCode.Ok,
      error: null,
      data: response.data,
      headers: response.headers,
    };
  }

  /* <summary>
  date: 29-11-2022
  Name: Gm
  description: Handle  Error response from WebAPI 
  <summary>*/
  HandleErrorResponse = (error) => {
    try {
      const response = {
        status: error.response !== undefined ? error.response.status : error,
        data: error.response,
        error,
      };

      return response;

      /* <summary>
        date: 24-02-2023
        Name: GM
        description: Sets the Session Timeout Modal Visibility to true
        <summary>*/
      // try {
      //   const mssg =
      //     error.response.data.message === "Token validation failed."
      //       ? MultipleLogin
      //       : sessionTimeout;

      //   store.dispatch(ToggleActionModal({ title: "", text: mssg }));
      // } catch (err) {
      //   store.dispatch(
      //     ToggleActionModal({ title: "Session Timeout!", text: sessionTimeout })
      //   );
      // }
    } catch (err) {
      return {
        status: 500,
        data: null,
        error: err,
      };
    }
  };

  async get(path, timeout, responseType) {
    clearTimeout(getServiceTimeout);

    const source = axios.CancelToken.source();

    getServiceTimeout = setTimeout(
      () => {
        source.cancel(TIMEOUT_ERROR);
      },
      timeout === undefined ? defaultTimeout : timeout
    );

    const response = await this.service.get(
      this.buildUrl(path),
      { responseType: responseType === undefined ? "json" : responseType },
      {
        cancelToken: source.token,
      }
    );

    clearTimeout(getServiceTimeout);
    getServiceTimeout = null;
    return response;
  }

  async saveRequest(path, payload, timeout) {
    clearTimeout(saveServiceTimeout);

    const source = axios.CancelToken.source();

    saveServiceTimeout = setTimeout(
      () => {
        source.cancel(TIMEOUT_ERROR);
      },
      timeout === undefined ? defaultTimeout : timeout
    );

    const response = await this.service.request({
      method: "POST",
      url: this.buildLongRunUrl(path),
      responseType: "json",
      data: payload,
      timeout: 60 * 7 * 1000,
    });

    clearTimeout(saveServiceTimeout);
    saveServiceTimeout = null;
    return response;
  }

  async post(path, payload, timeout) {
    clearTimeout(postServiceTimeout);

    const source = axios.CancelToken.source();

    postServiceTimeout = setTimeout(
      () => {
        source.cancel(TIMEOUT_ERROR);
      },
      timeout === undefined ? defaultTimeout : timeout
    );

    const response = await this.service.request({
      method: "POST",
      url: this.buildUrl(path),
      responseType: "json",
      data: payload,
      timeout: timeout === undefined ? defaultTimeout : timeout,
    });

    clearTimeout(postServiceTimeout);
    postServiceTimeout = null;
    return response;
  }

  /* <summary>
  date: 29-11-2022
  Name: Gm
  description: Post API Call with custom Header Data
  <summary>*/
  async postWithHeader(path, payload, timeout, header) {
    clearTimeout(postServiceWithHeaderTimeout);

    const source = axios.CancelToken.source();

    postServiceWithHeaderTimeout = setTimeout(
      () => {
        source.cancel(TIMEOUT_ERROR);
      },
      timeout === undefined ? defaultTimeout : timeout
    );

    const response = await this.service.request({
      method: "POST",
      url: this.buildUrl(path),
      responseType: "json",
      data: payload,
      cancelToken: source.token,
      header,
    });

    postServiceWithHeaderTimeout = null;
    return response;
  }

  async downloadBlob(path, timeout) {
    clearTimeout(getServiceTimeout);

    const source = axios.CancelToken.source();

    getServiceTimeout = setTimeout(
      () => {
        source.cancel(TIMEOUT_ERROR);
      },
      timeout === undefined ? defaultTimeout : timeout
    );

    const url = this.buildUrl(path);
    const response = await this.service.get(url, {
      cancelToken: source.token,
      responseType: "blob",
    });
    clearTimeout(getServiceTimeout);
    getServiceTimeout = null;
    return response;
  }
}

const webServiceWrapper = new WebServiceWrapper();

export default webServiceWrapper;
